/**
 * Snapchat Cookie Management Utility
 * Handles ScCid parameter extraction and _scid cookie management
 * Following Snapchat's Conversions API specifications
 */

/**
 * Generate a proper UUID v4 format
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c == 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

/**
 * Extract ScCid (Snapchat Click ID) from URL parameters
 * @param url - The URL to parse (defaults to current window location)
 * @returns The ScCid value if present, null otherwise
 */
export function extractSnapchatClickId(url?: string): string | null {
  const urlToParse = url || window.location.href
  const urlObj = new URL(urlToParse)
  const scCid = urlObj.searchParams.get('ScCid')

  // ScCid is case-sensitive, so we preserve the exact value
  return scCid
}

/**
 * Get existing _scid cookie value (Snapchat first-party cookie - Cookie 1)
 * @returns The _scid cookie value if present, null otherwise
 */
export function getSnapchatCookie(): string | null {
  const cookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=')
    acc[key] = value
    return acc
  }, {} as Record<string, string>)

  return cookies['_scid'] || null
}


/**
 * Create or update _scid cookie for first-party tracking (Cookie 1)
 * @param value - Optional value to set (generates UUID if not provided)
 * @returns The _scid cookie value
 */
export function createSnapchatCookie(value?: string): string {
  // Generate a proper UUID if no value provided
  const scidValue = value || (crypto?.randomUUID?.() ?? generateUUID())

  // Set cookie with 2-year expiration (common for first-party cookies)
  const expirationDate = new Date()
  expirationDate.setFullYear(expirationDate.getFullYear() + 2)

  // Set cookie with proper parameters
  // Using SameSite=Lax for compatibility with ad redirects
  document.cookie = `_scid=${scidValue}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`

  return scidValue
}


/**
 * Store Snapchat Click ID for session persistence
 * @param scCid - The ScCid value from URL
 */
export function storeSnapchatClickId(scCid: string): void {
  try {
    // Store in sessionStorage to persist across page navigation during the session
    sessionStorage.setItem('snapchat_click_id', scCid)
  } catch (error) {
    // Silent fail
  }
}

/**
 * Get stored Snapchat Click ID from session
 * @returns The stored ScCid value if present, null otherwise
 */
export function getStoredSnapchatClickId(): string | null {
  try {
    return sessionStorage.getItem('snapchat_click_id')
  } catch (error) {
    return null
  }
}

/**
 * Process Snapchat tracking parameters
 * This is the main function to call on page load or before checkout
 * @returns Object with sc_click_id and sc_cookie1 values
 */
export function processSnapchatCookies(): {
  sc_click_id: string | null;
  sc_cookie1: string | null;
} {
  // Check for ScCid in URL
  const scCid = extractSnapchatClickId()

  // Get existing _scid cookie (Cookie 1)
  let scCookie = getSnapchatCookie()

  // Handle Snapchat Click ID
  let clickId = scCid

  if (scCid) {
    // Store the click ID for later use
    storeSnapchatClickId(scCid)
  } else {
    // Check if we have a stored click ID from earlier in the session
    const storedClickId = getStoredSnapchatClickId()
    if (storedClickId) {
      clickId = storedClickId
    }
  }

  // Handle first-party cookie (Cookie 1)
  if (!scCookie) {
    // No existing cookie, create one for first-party tracking
    scCookie = createSnapchatCookie()
  }

  const result = {
    sc_click_id: clickId,
    sc_cookie1: scCookie
  }

  return result
}

/**
 * Initialize Snapchat tracking on page load
 * Should be called early in the application lifecycle
 */
export function initializeSnapchatTracking(): void {
  // Ensure we have first-party cookie (sc_cookie1 / _scid)
  if (!getSnapchatCookie()) {
    createSnapchatCookie()
  }

  // Process any click ID in the URL
  const scCid = extractSnapchatClickId()
  if (scCid) {
    storeSnapchatClickId(scCid)
  }
}